import * as React from 'react';
import { useTranslation } from 'next-i18next';
import tw, { styled } from 'twin.macro';
import Image from 'next/image';
import { useSetAtom } from 'jotai';

import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Spinner, IconButton, Link, showToast } from '@restworld/ui-ds';
import { usePaymentCompanyData } from '../hooks/use-payment-company-data';
import DetachPaymentMethodModal from '../modals/detach-payment-method-modal';
import { updatedDefaultPaymentMethodAtom } from '../use-job-positions-onboarding';
import { paymentProviderLogoMap } from '@restworld/utils-common';

interface PaymentMethod {
  id: string;
  customer: string;
  brand?: string;
  last4?: string;
  expiry_month?: number;
  expiry_year?: number;
  default?: boolean;
}

const Container = styled.div`
  ${tw`w-full flex flex-col gap-8`}
`;

const Title = styled.h2`
  ${tw`text-[22px] font-semibold text-[#1c1c33] text-center w-full leading-normal`}
`;

const LinkWrapper = styled.div`
  ${tw`flex items-center justify-center w-full`}
`;

const PaymentMethodList = styled.div`
  ${tw`flex flex-col gap-4 w-full`}
`;

const PaymentMethodCard = styled.button<{ isSelected: boolean }>`
  ${tw`w-full bg-[#fafafa] border rounded-lg p-3 grid items-center transition-all hover:border-primary-500 cursor-pointer shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)]`}
  ${({ isSelected }) => (isSelected ? tw`border-2 border-primary-500` : tw`border border-[#dedee1]`)}
  grid-template-columns: auto 1fr auto 100px;
  gap: 24px;
`;

const RadioButton = styled.div<{ isSelected: boolean }>`
  ${tw`w-4 h-4 rounded-full border flex items-center justify-center shrink-0`}
  ${({ isSelected }) =>
    isSelected ? tw`border-2 border-primary-500 bg-[#fafafa]` : tw`border border-[#858591] bg-[#fafafa]`}
`;

const RadioButtonInner = styled.div`
  ${tw`w-2 h-2 rounded-full bg-primary-500`}
`;

const PaymentMethodDetails = styled.div`
  ${tw`flex flex-col gap-1 items-start justify-center`}
`;

const CardBrandText = styled.span`
  ${tw`text-[14px] font-medium text-[#1c1c33] leading-normal`}
`;

const ExpiryText = styled.span`
  ${tw`text-[14px] text-[#1c1c33] font-medium whitespace-nowrap leading-normal`}
`;

const DefaultBadge = styled.span`
  ${tw`inline-flex items-center justify-center px-2 py-0.5 h-6 text-[14px] font-medium text-[#fafafa] bg-primary-500 rounded leading-[0]`}
  justify-self: end;
`;

const DeleteButtonWrapper = styled.div`
  justify-self: end;
`;

const EmptyState = styled.div`
  ${tw`text-center py-8 text-neutral-600`}
`;

const LoadingContainer = styled.div`
  ${tw`flex items-center justify-center py-12`}
`;

export interface ShowPaymentMethodsProps {
  /** Override navigation to 'add new payment method' screen */
  onNavigateToAddNew?: () => void;
  /** Render custom footer/CTA content below the payment methods list */
  renderFooter?: () => React.ReactNode;
}

export const ShowPaymentMethods: React.FC<ShowPaymentMethodsProps> = ({ onNavigateToAddNew, renderFooter }) => {
  const { t } = useTranslation(['jp-onboarding', 'payment-and-invoices']);
  const { paymentMethodsData, isLoading, detachPaymentMethod, isDetaching, isFetching } = usePaymentCompanyData();
  const setUpdatedDefaultPaymentMethod = useSetAtom(updatedDefaultPaymentMethodAtom);

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = React.useState<string | null>(null);
  const [isDetachModalOpen, setIsDetachModalOpen] = React.useState(false);
  const [paymentMethodToDetach, setPaymentMethodToDetach] = React.useState<PaymentMethod | null>(null);
  const initialDefaultPaymentMethodId = React.useRef<string | null>(null);

  // Get all payment methods from the organization
  const paymentMethods = React.useMemo(() => {
    return paymentMethodsData.flatMap((companyData) => companyData.payment_methods);
  }, [paymentMethodsData]);

  // Set initial selection to default payment method and store the initial default
  React.useEffect(() => {
    if (paymentMethods.length > 0 && !selectedPaymentMethodId) {
      const defaultMethod = paymentMethods.find((pm) => pm.default);
      const defaultId = defaultMethod?.id || paymentMethods[0]?.id || null;
      setSelectedPaymentMethodId(defaultId);
      initialDefaultPaymentMethodId.current = defaultId;
    }
  }, [paymentMethods, selectedPaymentMethodId]);

  // Handle payment method selection change
  const handlePaymentMethodSelect = React.useCallback(
    (paymentMethod: PaymentMethod) => {
      setSelectedPaymentMethodId(paymentMethod.id);

      // Update atom only if the selection is different from the initial default
      if (paymentMethod.id !== initialDefaultPaymentMethodId.current) {
        setUpdatedDefaultPaymentMethod({
          customerId: paymentMethod.customer,
          paymentMethodId: paymentMethod.id,
        });
      } else {
        // Reset atom if user selects back to original default
        setUpdatedDefaultPaymentMethod(null);
      }
    },
    [setUpdatedDefaultPaymentMethod]
  );

  // Reset atom when component unmounts
  React.useEffect(() => {
    return () => {
      setUpdatedDefaultPaymentMethod(null);
    };
  }, [setUpdatedDefaultPaymentMethod]);

  const handleAddNewPaymentMethod = React.useCallback(() => {
    onNavigateToAddNew?.();
  }, [onNavigateToAddNew]);

  const handleDetachPaymentMethodClick = React.useCallback((e: React.MouseEvent, paymentMethod: PaymentMethod) => {
    e.stopPropagation();
    setPaymentMethodToDetach(paymentMethod);
    setIsDetachModalOpen(true);
  }, []);

  const handleConfirmDetach = React.useCallback(async () => {
    if (!paymentMethodToDetach) return;

    try {
      // Call backend endpoint which securely calls Stripe API
      await detachPaymentMethod({ paymentMethodId: paymentMethodToDetach.id });

      // Show success toast
      showToast(t('steps.show_payment_methods.payment_method_detached_successfully'), 'success', {
        position: 'top-right',
        autoClose: 3000,
      });

      // If the deleted payment method was selected, reset selection to the new default
      if (selectedPaymentMethodId === paymentMethodToDetach.id) {
        const remainingMethods = paymentMethods.filter((pm) => pm.id !== paymentMethodToDetach.id);
        const newDefault = remainingMethods.find((pm) => pm.default);
        const newSelection = newDefault?.id || remainingMethods[0]?.id || null;
        setSelectedPaymentMethodId(newSelection);
        initialDefaultPaymentMethodId.current = newSelection;
        // Reset atom since we're back to default
        setUpdatedDefaultPaymentMethod(null);
      }

      // Close modal
      setIsDetachModalOpen(false);
      setPaymentMethodToDetach(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error detaching payment method:', error);
      showToast(t('steps.show_payment_methods.payment_method_detach_error'), 'error', {
        position: 'top-right',
        autoClose: 3000,
      });
      // Close modal even on error
      setIsDetachModalOpen(false);
      setPaymentMethodToDetach(null);
    }
  }, [
    detachPaymentMethod,
    paymentMethodToDetach,
    selectedPaymentMethodId,
    paymentMethods,
    setUpdatedDefaultPaymentMethod,
    t,
  ]);

  const handleCloseDetachModal = React.useCallback(() => {
    setIsDetachModalOpen(false);
    setPaymentMethodToDetach(null);
  }, []);

  if (isLoading || isFetching) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner color="primary" size="lg" />
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Title>{t('steps.show_payment_methods.title')}</Title>
      {paymentMethods?.length === 0 ? (
        <EmptyState>{t('steps.show_payment_methods.no_payment_methods')}</EmptyState>
      ) : (
        <PaymentMethodList>
          {paymentMethods?.map((paymentMethod) => {
            const isSelected = selectedPaymentMethodId === paymentMethod.id;
            const paymentBrand = paymentMethod.brand?.toLowerCase();
            const paymentLogoSrc = paymentBrand && paymentProviderLogoMap[paymentBrand];
            const showDeleteButton = !paymentMethod.default && paymentMethods.length > 1;

            return (
              <PaymentMethodCard
                key={paymentMethod.id}
                isSelected={isSelected}
                onClick={() => handlePaymentMethodSelect(paymentMethod)}
                type="button"
              >
                <RadioButton isSelected={isSelected}>{isSelected && <RadioButtonInner />}</RadioButton>

                <PaymentMethodDetails>
                  {paymentLogoSrc && (
                    <Image src={paymentLogoSrc} alt={paymentBrand || ''} width={23} height={16} tw="object-contain" />
                  )}
                  <CardBrandText>
                    {paymentMethod.brand} ***{paymentMethod.last4 || '****'}
                  </CardBrandText>
                </PaymentMethodDetails>

                <ExpiryText>
                  {t('steps.show_payment_methods.expires')}:<br />
                  {paymentMethod?.expiry_month && paymentMethod?.expiry_year
                    ? `${String(paymentMethod.expiry_month).padStart(2, '0')}/${String(paymentMethod.expiry_year).slice(
                        -2
                      )}`
                    : ''}
                </ExpiryText>

                {paymentMethod?.default ? (
                  <DefaultBadge>{t('steps.show_payment_methods.default')}</DefaultBadge>
                ) : showDeleteButton ? (
                  <DeleteButtonWrapper>
                    <IconButton
                      Icon={XMarkIcon}
                      ariaLabel="Delete-payment-method"
                      size="sm"
                      onClick={(e) => handleDetachPaymentMethodClick(e, paymentMethod)}
                    />
                  </DeleteButtonWrapper>
                ) : (
                  <div />
                )}
              </PaymentMethodCard>
            );
          })}
        </PaymentMethodList>
      )}

      <LinkWrapper>
        <Link StartIcon={PlusIcon} newTab={false} onClick={handleAddNewPaymentMethod}>
          {t('steps.show_payment_methods.add_new_payment_method')}
        </Link>
      </LinkWrapper>

      {renderFooter && renderFooter()}

      <DetachPaymentMethodModal
        isOpen={isDetachModalOpen}
        onClose={handleCloseDetachModal}
        onConfirm={handleConfirmDetach}
        paymentMethod={paymentMethodToDetach}
        isDetaching={isDetaching}
      />
    </Container>
  );
};

export default ShowPaymentMethods;
